import { useState, useMemo, useEffect, type FormEvent } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { knoxApi } from '@/lib/knox-api';
import { AuthPageShell } from '@/pages/login/AuthPageShell';

const PASSWORD_RULES = [
  { test: (v: string) => v.length >= 12, label: '12+ characters' },
  { test: (v: string) => /[a-zA-Z]/.test(v), label: 'Contains a letter' },
  { test: (v: string) => /[0-9]/.test(v), label: 'Contains a number' },
  { test: (v: string) => /[^a-zA-Z0-9]/.test(v), label: 'Contains a symbol' },
] as const;

const inputClass =
  'w-full rounded-full border border-black/[0.16] bg-surface-container-lowest py-4 px-6 text-foreground placeholder:text-muted-foreground/40 focus:border-primary/30 focus:outline-none focus:ring-2 focus:ring-primary/10 transition-all duration-300';

const btnPrimaryClass =
  'w-full rounded-full bg-gradient-to-br from-[#0070F3] to-[#005ac6] py-4 font-bold text-white shadow-lg transition-all duration-300 hover:shadow-[0_0_20px_rgba(135,173,255,0.3)] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed';

type VerifyState = 'loading' | 'valid' | 'invalid';

export function ResetPasswordPage() {
  const { t } = useTranslation('knox-aiops');
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const token = searchParams.get('token') ?? '';
  const [verifyState, setVerifyState] = useState<VerifyState>('loading');
  const [maskedEmail, setMaskedEmail] = useState('');

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const passwordErrors = useMemo(
    () => PASSWORD_RULES.filter((r) => !r.test(newPassword)).map((r) => r.label),
    [newPassword],
  );
  const isPasswordValid = passwordErrors.length === 0;
  const passwordsMatch = newPassword === confirmPassword && confirmPassword.length > 0;

  useEffect(() => {
    if (!token) {
      setVerifyState('invalid');
      return;
    }
    knoxApi
      .verifyResetToken(token)
      .then((result) => {
        if (result.valid) {
          setVerifyState('valid');
          setMaskedEmail(result.email ?? '');
        } else {
          setVerifyState('invalid');
        }
      })
      .catch(() => setVerifyState('invalid'));
  }, [token]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!isPasswordValid || !passwordsMatch) return;
    setSubmitting(true);
    try {
      const result = await knoxApi.resetPassword(token, newPassword, confirmPassword);
      toast.success(result.message);
      navigate('/login', { replace: true });
    } catch {
      // Error toast handled by knoxRequest
    } finally {
      setSubmitting(false);
    }
  }

  if (verifyState === 'loading') {
    return (
      <AuthPageShell>
        <div className="flex flex-col items-center gap-4">
          <span className="material-symbols-outlined animate-spin text-4xl text-primary">
            progress_activity
          </span>
          <p className="text-sm text-muted-foreground">{t('resetPassword.verifying')}</p>
        </div>
      </AuthPageShell>
    );
  }

  if (verifyState === 'invalid') {
    return (
      <AuthPageShell>
        <div className="flex flex-col items-center gap-6 text-center">
          <span className="material-symbols-outlined text-5xl text-destructive">link_off</span>
          <div>
            <h2 className="font-heading text-lg font-bold text-foreground mb-2">
              {t('resetPassword.linkExpired')}
            </h2>
            <p className="text-sm text-muted-foreground">
              {t('resetPassword.linkExpiredDescription')}
            </p>
          </div>
          <Link
            to="/forgot-password"
            className="text-sm text-primary hover:underline underline-offset-4"
          >
            {t('resetPassword.requestNewLink')}
          </Link>
        </div>
      </AuthPageShell>
    );
  }

  return (
    <AuthPageShell>
      <div className="mb-6 sm:mb-8 text-center">
        <div className="mb-3 flex justify-center">
          <img src="/knox-logo.png" alt="" className="h-10 sm:h-12 w-auto" />
        </div>
        <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight text-foreground mb-1">
          {t('resetPassword.title')}
        </h1>
        {maskedEmail && (
          <p className="text-sm text-muted-foreground">
            {t('resetPassword.for', { email: maskedEmail })}
          </p>
        )}
      </div>

      <form onSubmit={handleSubmit} className="flex flex-col gap-5">
        <div>
          <label
            htmlFor="new-password"
            className="mb-2 block px-1 text-[0.6875rem] font-bold uppercase tracking-widest text-muted-foreground"
          >
            {t('loginForm.createPassword')}
          </label>
          <input
            id="new-password"
            type="password"
            autoComplete="new-password"
            required
            autoFocus
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            placeholder={t('loginForm.passwordPlaceholder')}
            className={inputClass}
          />
          <p className="mt-1.5 px-1 text-xs text-muted-foreground/60">
            {t('loginForm.passwordRules')}
          </p>
          {newPassword.length > 0 && passwordErrors.length > 0 && (
            <ul className="text-xs text-destructive space-y-0.5 mt-1 px-1">
              {passwordErrors.map((e) => (
                <li key={e}>- {e}</li>
              ))}
            </ul>
          )}
        </div>

        <div>
          <label
            htmlFor="confirm-new-password"
            className="mb-2 block px-1 text-[0.6875rem] font-bold uppercase tracking-widest text-muted-foreground"
          >
            {t('loginForm.confirmPassword')}
          </label>
          <input
            id="confirm-new-password"
            type="password"
            autoComplete="new-password"
            required
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder={t('loginForm.confirmPasswordPlaceholder')}
            className={inputClass}
          />
          {confirmPassword.length > 0 && !passwordsMatch && (
            <p className="mt-1 px-1 text-xs text-destructive">
              {t('resetPassword.passwordMismatch')}
            </p>
          )}
        </div>

        <button
          type="submit"
          disabled={submitting || !isPasswordValid || !passwordsMatch}
          className={btnPrimaryClass}
        >
          {submitting ? t('resetPassword.resetting') : t('resetPassword.reset')}
        </button>
      </form>

      <div className="text-center mt-4">
        <Link to="/login" className="text-xs text-primary hover:underline underline-offset-4">
          {t('forgotPassword.backToLogin')}
        </Link>
      </div>
    </AuthPageShell>
  );
}
